import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import AiBottomSearchBar from "../components/ai/AiBottomSearchBar";
import { authenticationApi, sessionStorageService, AUTH_ROUTES } from "../features/authentication";
import "../styles/AskDiscoverWidget.css";
import "../styles/ai/ai-search.css";

const formatPrice = (price, priceRaw) => {
  if (typeof price === "string" && (price.includes("₹") || /Cr|L/i.test(price))) {
    return price;
  }
  const n = Number(priceRaw ?? price);
  if (!n || isNaN(n)) return "Price on request";
  if (n >= 10_000_000) return `₹ ${(n / 10_000_000).toFixed(2)} Cr`;
  if (n >= 100_000)    return `₹ ${(n / 100_000).toFixed(2)} L`;
  return `₹ ${n.toLocaleString("en-IN")}`;
};

/**
 * BUG FIX #3: Parse basic markdown (**bold**, * bullet lists) to JSX
 * so Gemini's formatted responses render properly instead of showing raw asterisks.
 */
function parseMarkdown(text) {
  if (!text) return null;

  const lines = text.split("\n");
  const elements = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (/^\s*[*\-]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[*\-]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[*\-]\s+/, ""));
        i++;
      }
      elements.push(
          <ul key={key++}>
            {items.map((item, j) => (
                <li key={j}>{inlineBold(item)}</li>
            ))}
          </ul>
      );
      continue;
    }

    if (line.trim() === "") {
      i++;
      continue;
    }

    elements.push(<p key={key++}>{inlineBold(line)}</p>);
    i++;
  }

  return elements;
}

function inlineBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

const ROBOT_IMG = "/images/ai-robot.png";
const API_BASE  = process.env.REACT_APP_API_BASE || "http://localhost:8080";
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyD1uIxkg-SEEcfAlBBFTpzjq6fhrTHbXc4";

async function askGemini(question, chatId, systemContext, history = [], token) {
  const res = await fetch(`${API_BASE}/api/ai/property-ask`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ question, chatId, systemContext, history }),
  });
  if (!res.ok) throw new Error(`${res.status}`);
  const d = await res.json();
  return d.message || "Sorry, I couldn't get a response.";
}

function buildHistory(messages) {
  return messages
      .filter(m => m.role === "user" || (m.role === "assistant" && m.content && !m.isPdfAsk))
      .slice(-12)
      .map(m => ({ role: m.role, content: m.content || "" }));
}

function buildPrompt(p) {
  if (!p) return `You are a helpful real estate assistant for OGM.
IMPORTANT: When asked about distance, routes, or nearby places, tell the user you are showing it on the interactive map below. Never say you cannot display a map.`;

  const price = formatPrice(p.price);
  return `You are an expert property advisor for One Global Marketplace (OGM).
Property: ${p.title} | Location: ${p.location} | Price: ${price}
Type: ${p.type} | ${p.bedrooms} BHK | ${p.bathrooms} Baths | ${p.builtupArea || p.sqft || ""} sqft
RERA: ${p.reraApproved ? "Yes" : "No"} | Facing: ${p.facing} | Parking: ${p.parking}
Amenities: ${(p.amenities || []).join(", ")}
Description: ${p.description || ""}

IMPORTANT RULES:
1. Be concise, helpful and positive.
2. You have an INTERACTIVE MAP widget. When asked about distance/routes, say "I'm showing the route on the map below" — NEVER say you cannot display a map.
3. When asked about nearby places (hospitals, schools, restaurants, etc.), say "I'm showing nearby options on the map below".
4. When a user shares their location (e.g. "I'm at RMZ Ecoworld"), acknowledge it and say the route map is shown below.
5. Format responses clearly. Use bullet points for lists.`;
}

function isDistanceQuery(q) {
  return /\bdistance\b|\broute\b|how far|\bfar\b|\bkm\b|kilometer|mile|\bdrive\b|\bwalk\b|commute|\breach\b|how.*get there|way to|direction|show.*map|route.*map|map.*route|\bto\b.*\bfrom\b|travel time|navigate|\bvia\b/i.test(q);
}

function extractDestination(q) {
  const myLocMatch = q.match(
      /(?:my\s+(?:current\s+)?(?:location|place|position)\s+is|i(?:'m| am)\s+(?:at|in|near)|from|starting from|starting at)\s+([a-zA-Z][a-zA-Z0-9 ,]+?)(?:\s+to|\s*[?.,!]|$)/i
  );
  if (myLocMatch) return { isUserLocation: true, place: myLocMatch[1].trim() };

  const toMatch = q.match(/(?:to|towards|reach|navigate to|direction.*?to)\s+([a-zA-Z][a-zA-Z0-9 ,]+?)(?:\s+from|\s*[?.,!]|$)/i);
  if (toMatch) return { isUserLocation: false, place: toMatch[1].trim() };

  const xyMatch = q.match(/([a-zA-Z][a-zA-Z0-9 ]+?)\s+to\s+([a-zA-Z][a-zA-Z0-9 ,]+)/i);
  if (xyMatch) return { isUserLocation: false, place: xyMatch[2].trim() };

  const cleaned = q
      .replace(/distance|route|how far|far|km|to|from|between|and|show me|map|route map|navigate|directions?|travel time/gi, " ")
      .replace(/\s+/g, " ").trim();
  return { isUserLocation: false, place: cleaned.length > 2 ? cleaned : null };
}

function MiniImageCarousel({ images, title }) {
  const [idx, setIdx] = useState(0);
  const all = images.filter(Boolean);
  if (!all.length) return null;

  const prev = (e) => { e.stopPropagation(); setIdx(i => (i - 1 + all.length) % all.length); };
  const next = (e) => { e.stopPropagation(); setIdx(i => (i + 1) % all.length); };

  return (
      <div className="adw-mini-imgs">
        <img src={all[idx]} alt={title} className="adw-mini-img-single" />
        {all.length > 1 && (
            <>
              <button className="adw-mini-arrow adw-mini-arrow-l" onClick={prev}>‹</button>
              <button className="adw-mini-arrow adw-mini-arrow-r" onClick={next}>›</button>
            </>
        )}
        <span className="adw-mini-count">{idx + 1}/{all.length}</span>
      </div>
  );
}

function buildDetailFromNew(pl) {
  return {
    name: pl.displayName?.text || "",
    formatted_address: pl.formattedAddress || "",
    rating: pl.rating,
    user_ratings_total: pl.userRatingCount,
    formatted_phone_number: pl.internationalPhoneNumber || null,
    website: pl.websiteUri || null,
    price_level: pl.priceLevel || null,
    opening_hours: pl.regularOpeningHours
        ? {
          isOpen: () => pl.regularOpeningHours.openNow ?? false,
          weekday_text: pl.regularOpeningHours.weekdayDescriptions || [],
        }
        : null,
    photos: (pl.photos || []).slice(0, 8).map((ph) => ({
      getUrl: ({ maxWidth = 640 } = {}) =>
          `https://places.googleapis.com/v1/${ph.name}/media?maxWidthPx=${maxWidth}&skipHttpRedirect=false&key=${GMAPS_KEY}`,
      name: ph.name,
    })),
    streetViewUrl: pl.location
        ? `https://maps.googleapis.com/maps/api/streetview?size=640x300&location=${pl.location.latitude},${pl.location.longitude}&fov=80&pitch=0&key=${GMAPS_KEY}`
        : null,
  };
}

function SmartMap({ origin, destination, nearbyType, nearbyKeyword, onPlaceClick, propLat, propLng, isUserOrigin }) {
  const [distance,      setDistance]      = useState(null);
  const [duration,      setDuration]      = useState(null);
  const [places,        setPlaces]        = useState([]);
  const [mode,          setMode]          = useState("loading");
  const [routingTo,     setRoutingTo]     = useState(null);
  const [routeDistance, setRouteDistance] = useState(null);
  const [routeDuration, setRouteDuration] = useState(null);
  const mapRef        = useRef(null);
  const mapObj        = useRef(null);
  const markersRef    = useRef([]);
  const propertyLatLng = useRef(null);
  const dirRendererRef = useRef(null);

  const geocodePlace = async (text, biasLatLng = null) => {
    const coordMatch = text && text.match(/^(-?\d+\.\d+),\s*(-?\d+\.\d+)$/);
    if (coordMatch) {
      const ll = new window.google.maps.LatLng(parseFloat(coordMatch[1]), parseFloat(coordMatch[2]));
      return { latLng: ll, formatted: text };
    }

    const request = {
      address: text,
      componentRestrictions: { country: "in" },
    };

    if (biasLatLng) {
      request.bounds = new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(biasLatLng.lat() - 0.45, biasLatLng.lng() - 0.45),
          new window.google.maps.LatLng(biasLatLng.lat() + 0.45, biasLatLng.lng() + 0.45)
      );
    }

    return new Promise((resolve) => {
      new window.google.maps.Geocoder().geocode(request, (res, st) => {
        if (st === "OK" && res[0]) {
          resolve({ latLng: res[0].geometry.location, formatted: res[0].formatted_address });
        } else {
          resolve(null);
        }
      });
    });
  };

  const propertyMarkerIcon = () => ({
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="52" height="62" viewBox="0 0 52 62">
        <ellipse cx="26" cy="59" rx="12" ry="3" fill="rgba(0,0,0,0.18)"/>
        <path d="M26 2 C13 2 4 11 4 22 C4 37 26 58 26 58 C26 58 48 37 48 22 C48 11 39 2 26 2 Z"
              fill="#1B3A6B" stroke="#fff" stroke-width="2"/>
        <polygon points="26,10 38,20 34,20 34,34 18,34 18,20 14,20" fill="#fff" opacity="0.95"/>
        <rect x="22" y="25" width="8" height="9" rx="1" fill="#1B3A6B"/>
        <rect x="19" y="19" width="5" height="4" rx="0.5" fill="#f97316"/>
        <rect x="28" y="19" width="5" height="4" rx="0.5" fill="#f97316"/>
        <rect x="31" y="11" width="3" height="5" fill="#fff" opacity="0.7"/>
      </svg>`
    ),
    scaledSize: new window.google.maps.Size(52, 62),
    anchor: new window.google.maps.Point(26, 58),
  });

  const addPropertyLabel = (map, position) => {
    const iw = new window.google.maps.InfoWindow({
      content: `<div style="font-family:'DM Sans',sans-serif;font-size:11px;font-weight:800;color:#1B3A6B;background:#fff;padding:3px 9px;border-radius:20px;border:1.5px solid #e2e8f0;box-shadow:0 2px 8px rgba(15,23,42,.12);white-space:nowrap;">📍 This Property</div>`,
      disableAutoPan: true,
      pixelOffset: new window.google.maps.Size(0, -66),
    });
    iw.open(map);
    iw.setPosition(position);
    window.google.maps.event.addListenerOnce(iw, "domready", () => {
      const btn = document.querySelector(".gm-ui-hover-effect");
      if (btn) btn.style.display = "none";
      const tail = document.querySelector(".gm-style .gm-style-iw-tc");
      if (tail) tail.style.display = "none";
    });
  };

  useEffect(() => {
    if (!destination && !nearbyKeyword) { setMode("error"); return; }
    if (!origin) { setMode("error"); return; }

    const init = async () => {
      if (!mapRef.current || !window.google?.maps) return;
      const map = new window.google.maps.Map(mapRef.current, {
        zoom: 13,
        center: { lat: 12.9716, lng: 77.5946 },
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_CENTER },
        styles: [
          { featureType: "poi.business", stylers: [{ visibility: "off" }] },
          { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
        ],
      });
      mapObj.current = map;

      if (nearbyKeyword) {
        let center, lat, lng;
        if (propLat !== null && propLng !== null) {
          lat = propLat; lng = propLng;
          center = new window.google.maps.LatLng(lat, lng);
        } else {
          const geo = await geocodePlace(origin, null);
          if (!geo) { setMode("error"); return; }
          center = geo.latLng;
          lat = center.lat();
          lng = center.lng();
        }
        propertyLatLng.current = center;
        map.setCenter(center); map.setZoom(14);

        new window.google.maps.Marker({
          position: center, map,
          icon: propertyMarkerIcon(),
          title: "This Property", zIndex: 999,
          animation: window.google.maps.Animation.DROP,
        });
        addPropertyLabel(map, center);

        try {
          const resp = await fetch(
              "https://places.googleapis.com/v1/places:searchNearby",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  "X-Goog-Api-Key": GMAPS_KEY,
                  "X-Goog-FieldMask": [
                    "places.id",
                    "places.displayName",
                    "places.formattedAddress",
                    "places.location",
                    "places.rating",
                    "places.userRatingCount",
                    "places.regularOpeningHours",
                    "places.internationalPhoneNumber",
                    "places.photos",
                    "places.priceLevel",
                    "places.websiteUri",
                  ].join(","),
                },
                body: JSON.stringify({
                  includedTypes: [nearbyType],
                  maxResultCount: 8,
                  rankPreference: "DISTANCE",
                  locationRestriction: {
                    circle: { center: { latitude: lat, longitude: lng }, radius: 4000 },
                  },
                }),
              }
          );
          const data = await resp.json();
          const results = (data.places || []).slice(0, 8);
          if (!results.length) { setMode("error"); return; }

          const normalised = results.map((pl) => ({
            place_id: pl.id,
            name: pl.displayName?.text || "",
            vicinity: pl.formattedAddress || "",
            rating: pl.rating,
            ratingCount: pl.userRatingCount,
            geometry: {
              location: new window.google.maps.LatLng(pl.location.latitude, pl.location.longitude),
            },
            _raw: pl,
          }));

          setPlaces(normalised);
          setMode("nearby");

          const bounds = new window.google.maps.LatLngBounds();
          bounds.extend(center);

          markersRef.current = [];
          normalised.forEach((pl, i) => {
            bounds.extend(pl.geometry.location);
            const mk = new window.google.maps.Marker({
              position: pl.geometry.location, map,
              label: { text: String(i + 1), color: "#fff", fontSize: "11px", fontWeight: "700" },
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE, scale: 14,
                fillColor: "#f97316", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2,
              },
              title: pl.name,
              zIndex: i + 1,
              animation: window.google.maps.Animation.DROP,
            });
            markersRef.current.push(mk);
            mk.addListener("click", () => showRouteTo(pl, i));
          });
          map.fitBounds(bounds, { padding: 60 });
        } catch (e) {
          console.error("Places API error:", e);
          setMode("error");
        }

      } else if (destination) {
        const propCenter = (propLat !== null && propLng !== null)
            ? new window.google.maps.LatLng(propLat, propLng) : null;

        const [originGeo, destGeo] = await Promise.all([
          isUserOrigin
              ? geocodePlace(origin, propCenter)
              : Promise.resolve(propCenter ? { latLng: propCenter, formatted: origin } : null),
          destination ? geocodePlace(destination, propCenter) : Promise.resolve(null),
        ]);

        if (!originGeo || !destGeo) { setMode("error"); return; }

        new window.google.maps.Marker({
          position: propCenter || originGeo.latLng, map,
          icon: propertyMarkerIcon(),
          title: "This Property", zIndex: 100,
          animation: window.google.maps.Animation.DROP,
        });

        const ds = new window.google.maps.DirectionsService();
        const dr = new window.google.maps.DirectionsRenderer({
          map,
          suppressMarkers: false,
          polylineOptions: { strokeColor: "#f97316", strokeWeight: 5, strokeOpacity: 0.9 },
        });
        ds.route(
            { origin: originGeo.latLng, destination: destGeo.latLng, travelMode: "DRIVING" },
            (res, status) => {
              if (status === "OK") {
                dr.setDirections(res);
                const leg = res.routes[0]?.legs[0];
                if (leg) { setDistance(leg.distance?.text); setDuration(leg.duration?.text); }
                setMode("route");
              } else { setMode("error"); }
            }
        );
      }
    };

    if (typeof window.google?.maps?.Map === "function") {
      init();
    } else if (!document.getElementById("gmaps-sdk")) {
      const s = document.createElement("script");
      s.id = "gmaps-sdk";
      s.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&libraries=places,geometry`;
      s.async = true; s.defer = true;
      s.onload = () => init();
      document.head.appendChild(s);
    } else {
      const t = setInterval(() => {
        if (typeof window.google?.maps?.Map === "function") { clearInterval(t); init(); }
      }, 150);
      return () => clearInterval(t);
    }
  }, [origin, destination, nearbyType, nearbyKeyword]);

  const showRouteTo = useCallback((pl, idx) => {
    if (!mapObj.current || !propertyLatLng.current) return;
    const map = mapObj.current;

    if (dirRendererRef.current) {
      dirRendererRef.current.setMap(null);
      dirRendererRef.current = null;
    }

    markersRef.current.forEach((mk, i) => {
      mk.setIcon({
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: i === idx ? 16 : 11,
        fillColor: i === idx ? "#1B3A6B" : "#f97316",
        fillOpacity: i === idx ? 1 : 0.4,
        strokeColor: "#fff",
        strokeWeight: 2,
      });
      mk.setLabel({
        text: String(i + 1),
        color: "#fff",
        fontSize: i === idx ? "12px" : "10px",
        fontWeight: "700",
      });
      mk.setZIndex(i === idx ? 500 : i + 1);
    });

    setRoutingTo({ name: pl.name, idx });
    setRouteDistance(null);
    setRouteDuration(null);

    const ds = new window.google.maps.DirectionsService();
    const dr = new window.google.maps.DirectionsRenderer({
      map,
      suppressMarkers: true,
      polylineOptions: { strokeColor: "#1B3A6B", strokeWeight: 5, strokeOpacity: 0.85 },
    });
    dirRendererRef.current = dr;

    ds.route(
        { origin: propertyLatLng.current, destination: pl.geometry.location, travelMode: "DRIVING" },
        (res, status) => {
          if (status === "OK") {
            dr.setDirections(res);
            const leg = res.routes[0]?.legs[0];
            if (leg) {
              setRouteDistance(leg.distance?.text);
              setRouteDuration(leg.duration?.text);
            }
          }
        }
    );
  }, []);

  const clearRoute = useCallback(() => {
    if (dirRendererRef.current) {
      dirRendererRef.current.setMap(null);
      dirRendererRef.current = null;
    }
    markersRef.current.forEach((mk, i) => {
      mk.setIcon({
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 14,
        fillColor: "#f97316",
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: 2,
      });
      mk.setLabel({ text: String(i + 1), color: "#fff", fontSize: "11px", fontWeight: "700" });
      mk.setZIndex(i + 1);
    });
    setRoutingTo(null);
    setRouteDistance(null);
    setRouteDuration(null);
    if (mapObj.current && markersRef.current.length) {
      const bounds = new window.google.maps.LatLngBounds();
      if (propertyLatLng.current) bounds.extend(propertyLatLng.current);
      markersRef.current.forEach(mk => bounds.extend(mk.getPosition()));
      mapObj.current.fitBounds(bounds, { padding: 60 });
    }
  }, []);

  return (
      <div className="adw-map-outer">
        <div className="adw-map-wrap">
          <div ref={mapRef} className="adw-map" />
          {mode === "loading" && (
              <div className="adw-map-loading">
                <span style={{ fontSize: 20, marginBottom: 6 }}>🗺️</span> Loading map…
              </div>
          )}
          {mode === "route" && (distance || duration) && (
              <div className="adw-map-badge">
                <span className="adw-map-dist">📍 {distance}</span>
                <span className="adw-map-sep">•</span>
                <span className="adw-map-dur">🕐 {duration} by car</span>
              </div>
          )}
          {mode === "nearby" && !routingTo && (
              <div className="adw-map-nearby-hint">Tap a number to get route</div>
          )}
          {mode === "nearby" && routingTo && (
              <div className="adw-map-route-bar">
                <button className="adw-map-route-back" onClick={clearRoute}>← Back</button>
                <div className="adw-map-route-info">
                  <span className="adw-map-route-name">→ {routingTo.name}</span>
                  {routeDistance && <span className="adw-map-route-dist">📍 {routeDistance} • 🕐 {routeDuration}</span>}
                  {!routeDistance && <span className="adw-map-route-dist">Calculating…</span>}
                </div>
              </div>
          )}
          {mode === "error" && (
              <div className="adw-map-error">
                <span style={{ fontSize: 28 }}>🗺️</span>
                Could not load map. Try a more specific place name.
              </div>
          )}
        </div>
        {mode === "nearby" && places.length > 0 && (
            <div className="adw-nearby-list">
              <div className="adw-nearby-header">{places.length} {nearbyKeyword}s found nearby</div>
              {places.map((pl, i) => (
                  <button
                      key={pl.place_id}
                      className={`adw-nearby-item${routingTo?.idx === i ? " adw-nearby-item-active" : ""}`}
                      onClick={() => showRouteTo(pl, i)}
                  >
                    <span className={`adw-nearby-num${routingTo?.idx === i ? " adw-nearby-num-active" : ""}`}>{i + 1}</span>
                    <div className="adw-nearby-info">
                      <div className="adw-nearby-name">{pl.name}</div>
                      {pl.vicinity && <div className="adw-nearby-addr">{pl.vicinity}</div>}
                      {pl.rating && (
                          <div className="adw-nearby-rating">
                            {"★".repeat(Math.round(pl.rating))}{"☆".repeat(5 - Math.round(pl.rating))}{" "}
                            {pl.rating}{pl.ratingCount ? ` (${pl.ratingCount.toLocaleString()})` : ""}
                          </div>
                      )}
                    </div>
                    <div className="adw-nearby-item-right">
                      {routingTo?.idx === i && routeDistance ? (
                          <span className="adw-nearby-route-badge">{routeDistance}</span>
                      ) : (
                          <span className="adw-nearby-arrow">›</span>
                      )}
                      <button
                          className="adw-nearby-detail-btn"
                          onClick={e => { e.stopPropagation(); onPlaceClick(buildDetailFromNew(pl._raw || {}), i + 1); }}
                          title="View details"
                      >ℹ</button>
                    </div>
                  </button>
              ))}
            </div>
        )}
      </div>
  );
}

function priceLevelLabel(level) {
  if (!level) return null;
  const map = { PRICE_LEVEL_FREE: "Free", PRICE_LEVEL_INEXPENSIVE: "₹", PRICE_LEVEL_MODERATE: "₹₹", PRICE_LEVEL_EXPENSIVE: "₹₹₹", PRICE_LEVEL_VERY_EXPENSIVE: "₹₹₹₹" };
  return map[level] || null;
}

function PlacePanel({ place, number, onClose }) {
  const [photoIdx,    setPhotoIdx]    = useState(0);
  const [hoursOpen,   setHoursOpen]   = useState(false);
  if (!place) return null;
  const photos   = place.photos?.slice(0, 8) || [];
  const isOpen   = place.opening_hours?.isOpen?.();
  const weekdays = place.opening_hours?.weekday_text || [];
  const price    = priceLevelLabel(place.price_level);
  const gmapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name + " " + (place.formatted_address || ""))}`;

  return (
      <div className="adw-place-panel">
        <div className="adw-place-header">
          <div className="adw-place-header-num">{number}</div>
          <span className="adw-place-header-title">{place.name}</span>
          <button className="adw-place-header-close" onClick={onClose}>✕</button>
        </div>

        {photos.length > 0 ? (
            <div className="adw-place-photo-wrap">
              <img
                  src={photos[photoIdx].getUrl({ maxWidth: 800 })}
                  alt={place.name}
                  className="adw-place-photo"
                  onError={(e) => {
                    if (place.streetViewUrl && e.target.src !== place.streetViewUrl) {
                      e.target.src = place.streetViewUrl;
                    } else {
                      e.target.closest(".adw-place-photo-wrap").style.display = "none";
                      const np = e.target.closest(".adw-place-photo-wrap").nextSibling;
                      if (np) np.style.display = "flex";
                    }
                  }}
              />
              {photos.length > 1 && (
                  <>
                    <button className="adw-place-photo-arrow left" onClick={() => setPhotoIdx(i => (i - 1 + photos.length) % photos.length)}>‹</button>
                    <button className="adw-place-photo-arrow right" onClick={() => setPhotoIdx(i => (i + 1) % photos.length)}>›</button>
                    <div className="adw-place-photo-dots">
                      {photos.map((_, i) => (
                          <button key={i} className={"adw-place-dot" + (i === photoIdx ? " active" : "")} onClick={() => setPhotoIdx(i)} />
                      ))}
                    </div>
                    <span className="adw-place-photo-count">{photoIdx + 1}/{photos.length}</span>
                  </>
              )}
            </div>
        ) : place.streetViewUrl ? (
            <div className="adw-place-photo-wrap">
              <img
                  src={place.streetViewUrl}
                  alt={"Street view of " + place.name}
                  className="adw-place-photo"
                  onError={e => { e.target.closest(".adw-place-photo-wrap").style.display = "none"; }}
              />
              <div className="adw-place-sv-badge">📸 Street View</div>
            </div>
        ) : (
            <div className="adw-place-no-photo">
              <span style={{fontSize:32}}>🏢</span>
              <span>No photos available</span>
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place.name)}`}
                 target="_blank" rel="noreferrer" className="adw-place-gmaps-link">
                View on Google Maps →
              </a>
            </div>
        )}

        <div className="adw-place-body">

          <div className="adw-place-title-row">
            <h3 className="adw-place-name">{place.name}</h3>
            {place.opening_hours && (
                <span className={"adw-place-status " + (isOpen ? "open" : "closed")}>
              {isOpen ? "Open" : "Closed"}
            </span>
            )}
          </div>

          {place.rating && (
              <div className="adw-place-rating-row">
                <div className="adw-place-stars">
                  {[1,2,3,4,5].map(s => (
                      <span key={s} style={{ color: s <= Math.round(place.rating) ? "#f97316" : "#e2e8f0", fontSize: 15 }}>★</span>
                  ))}
                </div>
                <span className="adw-place-rating-num">{place.rating}</span>
                {place.user_ratings_total && (
                    <span className="adw-place-rating-count">({place.user_ratings_total.toLocaleString()} reviews)</span>
                )}
                {price && <span className="adw-place-price">{price}</span>}
              </div>
          )}

          <div className="adw-place-chips">
            {place.formatted_phone_number && (
                <a href={`tel:${place.formatted_phone_number}`} className="adw-place-chip adw-place-chip-phone">
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.6 3.27 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 5.99 5.99l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  {place.formatted_phone_number}
                </a>
            )}
            {place.website && (
                <a href={place.website} target="_blank" rel="noreferrer" className="adw-place-chip adw-place-chip-web">
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                  Website
                </a>
            )}
          </div>

          {place.formatted_address && (
              <div className="adw-place-info-row">
                <span className="adw-place-info-icon">📍</span>
                <span className="adw-place-info-text">{place.formatted_address}</span>
              </div>
          )}

          {weekdays.length > 0 && (
              <div className="adw-place-hours-section">
                <button className="adw-place-hours-toggle" onClick={() => setHoursOpen(v => !v)}>
                  <span className="adw-place-info-icon">🕐</span>
                  <span className={"adw-place-hours-status " + (isOpen ? "open" : "closed")}>
                {isOpen ? "Open now" : "Closed now"}
              </span>
                  <span className="adw-place-hours-chevron">{hoursOpen ? "▲" : "▼"}</span>
                </button>
                {hoursOpen && (
                    <div className="adw-place-hours-list">
                      {weekdays.map((line, i) => {
                        const [day, ...rest] = line.split(": ");
                        const today = new Date().toLocaleDateString("en-US", { weekday: "long" });
                        const isToday = line.startsWith(today);
                        return (
                            <div key={i} className={"adw-place-hours-row" + (isToday ? " today" : "")}>
                              <span className="adw-hours-day">{day}</span>
                              <span className="adw-hours-time">{rest.join(": ")}</span>
                            </div>
                        );
                      })}
                    </div>
                )}
              </div>
          )}

          <div className="adw-place-actions">
            <a href={gmapsUrl} target="_blank" rel="noreferrer" className="adw-place-action-btn primary">
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
              Get Directions
            </a>
            {place.formatted_phone_number && (
                <a href={`tel:${place.formatted_phone_number}`} className="adw-place-action-btn secondary">
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.6 3.27 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 5.99 5.99l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                  Call
                </a>
            )}
          </div>

        </div>
      </div>
  );
}

function isNearbyQuery(q) {
  return /hotel|resort|stay|accommodation|hospital|school|college|university|mall|restaurant|food|dine|eating|cafe|coffee|park|garden|metro|station|supermarket|grocery|pharmacy|chemist|gym|fitness|yoga|temple|church|mosque|gurudwara|cinema|theatre|petrol|fuel|atm|bank|salon|spa|laundry|daycare|nursery|bakery|bar|pub|clinic|diagnostic|lab|police|fire|post.*office|library|swimming|pool|nearby|near|close to|around|next to/i.test(q);
}
function extractNearbyType(q) {
  const types = [
    { k: "tech park",        t: "establishment",      label: "tech park" },
    { k: "it park",          t: "establishment",      label: "IT park" },
    { k: "post office",      t: "post_office",        label: "post office" },
    { k: "accommodation",    t: "lodging",            label: "accommodation" },
    { k: "supermarket",      t: "supermarket",        label: "supermarket" },
    { k: "swimming pool",    t: "swimming_pool",      label: "swimming pool" },
    { k: "day care",         t: "child_care_agency",  label: "daycare" },
    { k: "daycare",          t: "child_care_agency",  label: "daycare" },
    { k: "diagnostic",       t: "medical_lab",        label: "diagnostic centre" },
    { k: "gurudwara",        t: "hindu_temple",       label: "gurudwara" },
    { k: "restaurant",       t: "restaurant",         label: "restaurant" },
    { k: "hospital",         t: "hospital",           label: "hospital" },
    { k: "university",       t: "university",         label: "university" },
    { k: "college",          t: "university",         label: "college" },
    { k: "pharmacy",         t: "pharmacy",           label: "pharmacy" },
    { k: "chemist",          t: "pharmacy",           label: "pharmacy" },
    { k: "grocery",          t: "grocery_store",      label: "grocery store" },
    { k: "fitness",          t: "gym",                label: "gym" },
    { k: "clinic",           t: "doctor",             label: "clinic" },
    { k: "theatre",          t: "movie_theater",      label: "theatre" },
    { k: "cinema",           t: "movie_theater",      label: "cinema" },
    { k: "temple",           t: "hindu_temple",       label: "temple" },
    { k: "church",           t: "church",             label: "church" },
    { k: "mosque",           t: "mosque",             label: "mosque" },
    { k: "resort",           t: "lodging",            label: "resort" },
    { k: "hotel",            t: "lodging",            label: "hotel" },
    { k: "school",           t: "school",             label: "school" },
    { k: "garden",           t: "park",               label: "garden" },
    { k: "metro",            t: "subway_station",     label: "metro station" },
    { k: "station",          t: "transit_station",    label: "transit station" },
    { k: "petrol",           t: "gas_station",        label: "petrol station" },
    { k: "fuel",             t: "gas_station",        label: "fuel station" },
    { k: "bakery",           t: "bakery",             label: "bakery" },
    { k: "salon",            t: "beauty_salon",       label: "salon" },
    { k: "spa",              t: "spa",                label: "spa" },
    { k: "yoga",             t: "gym",                label: "yoga studio" },
    { k: "laundry",          t: "laundry",            label: "laundry" },
    { k: "library",          t: "library",            label: "library" },
    { k: "police",           t: "police",             label: "police station" },
    { k: "coffee",           t: "cafe",               label: "coffee shop" },
    { k: "cafe",             t: "cafe",               label: "cafe" },
    { k: "park",             t: "park",               label: "park" },
    { k: "mall",             t: "shopping_mall",      label: "mall" },
    { k: "bank",             t: "bank",               label: "bank" },
    { k: "atm",              t: "atm",                label: "ATM" },
    { k: "food",             t: "restaurant",         label: "restaurant" },
    { k: "dine",             t: "restaurant",         label: "restaurant" },
    { k: "eating",           t: "restaurant",         label: "restaurant" },
    { k: "stay",             t: "lodging",            label: "hotel" },
    { k: "bar",              t: "bar",                label: "bar" },
    { k: "pub",              t: "bar",                label: "pub" },
    { k: "gym",              t: "gym",                label: "gym" },
    { k: "workout",          t: "gym",                label: "gym" },
    { k: "nursery",          t: "school",             label: "nursery school" },
    { k: "pool",             t: "swimming_pool",      label: "swimming pool" },
    { k: "shop",             t: "store",              label: "store" },
    { k: "medical",          t: "hospital",           label: "medical centre" },
    { k: "lab",              t: "medical_lab",        label: "lab" },
  ];
  const lq = q.toLowerCase();
  for (const { k, t, label } of types) {
    if (lq.includes(k)) return { keyword: label, type: t };
  }
  return { keyword: "place", type: "point_of_interest" };
}

const DEFAULT_SUGGESTIONS = [
  "What are the key highlights?",
  "Is the price negotiable?",
  "What schools are nearby?",
  "How do I book a site visit?",
];
const POST_QUERY_SUGGESTIONS = [
  "Are there good restaurants nearby?",
  "Show hotels near this property",
  "Are there good hospitals nearby?",
  "What gyms are close by?",
  "Show temples nearby",
  "What's the distance to Whitefield?",
];

function isPdfQuery(q) {
  return /pdf|document|download|brochure|report|save.*details|create.*doc|generate.*doc|compile/i.test(q);
}

function isEmiQuery(q) {
  const lq = q.toLowerCase().trim();

  const isKnowledgeQ = /^(what is|what are|what's|how does|why is|explain|tell me about|describe|define|what do you mean|meaning of|basics of|difference between)/i.test(lq)
      && !/calculate|show me|compute|my emi|for this property|how much (will i|do i|would i)|can i afford/i.test(lq);

  if (isKnowledgeQ) return false;

  return /\bemi\b/i.test(lq)
      || /calculate.*(loan|emi|mortgage|monthly|payment)/i.test(lq)
      || /monthly.*(emi|installment|payment)/i.test(lq)
      || /how much.*(pay|emi|loan|afford|monthly)/i.test(lq)
      || /(can i|can we).*(afford|pay)/i.test(lq)
      || /loan.*(this|property|price|amount)/i.test(lq)
      || /down payment.*calculat/i.test(lq)
      || /monthly installment/i.test(lq)
      || /home loan (amount|eligibility|calculator)/i.test(lq);
}

function EmiCalculator({ propertyPrice, onClose }) {
  const rawPrice = typeof propertyPrice === "number" ? propertyPrice
      : (() => {
        if (!propertyPrice) return 5000000;
        const s = String(propertyPrice).replace(/[₹,\s]/g, "");
        if (/Cr/i.test(s)) return parseFloat(s) * 10000000;
        if (/L/i.test(s))  return parseFloat(s) * 100000;
        return parseFloat(s) || 5000000;
      })();

  const [loanAmt,    setLoanAmt]    = useState(Math.round(rawPrice * 0.8));
  const [rate,       setRate]       = useState(8.5);
  const [tenure,     setTenure]     = useState(20);
  const [downPct,    setDownPct]    = useState(20);

  const handleDownPct = (val) => {
    setDownPct(val);
    setLoanAmt(Math.round(rawPrice * (1 - val / 100)));
  };

  const monthlyRate = rate / 12 / 100;
  const n = tenure * 12;
  const emi = monthlyRate > 0
      ? Math.round(loanAmt * monthlyRate * Math.pow(1 + monthlyRate, n) / (Math.pow(1 + monthlyRate, n) - 1))
      : Math.round(loanAmt / n);
  const totalPayable = emi * n;
  const totalInterest = totalPayable - loanAmt;
  const downPayment = rawPrice - loanAmt;

  const fmt = (n) => n >= 10000000
      ? `₹${(n/10000000).toFixed(2)} Cr`
      : n >= 100000
          ? `₹${(n/100000).toFixed(2)} L`
          : `₹${n.toLocaleString("en-IN")}`;

  const principalPct = Math.round((loanAmt / totalPayable) * 100);

  return (
      <div className="adw-emi-card">
        <div className="adw-emi-header">
          <span className="adw-emi-title">🏦 EMI Calculator</span>
          {onClose && <button className="adw-emi-close" onClick={onClose}>✕</button>}
        </div>

        <div className="adw-emi-result">
          <div className="adw-emi-result-main">
            <span className="adw-emi-label">Monthly EMI</span>
            <span className="adw-emi-amount">{fmt(emi)}</span>
          </div>
          <div className="adw-emi-result-sub">
            <div className="adw-emi-sub-item">
              <span className="adw-emi-sub-label">Down Payment</span>
              <span className="adw-emi-sub-val">{fmt(downPayment)}</span>
            </div>
            <div className="adw-emi-sub-item">
              <span className="adw-emi-sub-label">Total Interest</span>
              <span className="adw-emi-sub-val red">{fmt(totalInterest)}</span>
            </div>
            <div className="adw-emi-sub-item">
              <span className="adw-emi-sub-label">Total Payable</span>
              <span className="adw-emi-sub-val">{fmt(totalPayable)}</span>
            </div>
          </div>
          <div className="adw-emi-bar-wrap">
            <div className="adw-emi-bar">
              <div className="adw-emi-bar-fill principal" style={{ width: principalPct + "%" }} />
            </div>
            <div className="adw-emi-bar-legend">
              <span><span className="adw-emi-dot principal"/>Principal {principalPct}%</span>
              <span><span className="adw-emi-dot interest"/>Interest {100 - principalPct}%</span>
            </div>
          </div>
        </div>

        <div className="adw-emi-controls">
          <div className="adw-emi-control">
            <div className="adw-emi-control-header">
              <span>Down Payment</span>
              <span className="adw-emi-control-val">{downPct}% — {fmt(downPayment)}</span>
            </div>
            <input type="range" min="5" max="50" step="5" value={downPct}
                   onChange={e => handleDownPct(Number(e.target.value))}
                   className="adw-emi-slider" />
            <div className="adw-emi-slider-labels"><span>5%</span><span>50%</span></div>
          </div>

          <div className="adw-emi-control">
            <div className="adw-emi-control-header">
              <span>Interest Rate</span>
              <span className="adw-emi-control-val">{rate}% p.a.</span>
            </div>
            <input type="range" min="6" max="14" step="0.5" value={rate}
                   onChange={e => setRate(Number(e.target.value))}
                   className="adw-emi-slider" />
            <div className="adw-emi-slider-labels"><span>6%</span><span>14%</span></div>
          </div>

          <div className="adw-emi-control">
            <div className="adw-emi-control-header">
              <span>Loan Tenure</span>
              <span className="adw-emi-control-val">{tenure} years</span>
            </div>
            <input type="range" min="5" max="30" step="5" value={tenure}
                   onChange={e => setTenure(Number(e.target.value))}
                   className="adw-emi-slider" />
            <div className="adw-emi-slider-labels"><span>5 yrs</span><span>30 yrs</span></div>
          </div>
        </div>

        <div className="adw-emi-note">
          * Based on {fmt(loanAmt)} loan at {rate}% for {tenure} years. Actual rates may vary.
        </div>
      </div>
  );
}

/**
 * Chat history is only saved once the user is logged in - it lives in the
 * database tied to their account (authenticationApi.listChatHistory /
 * saveChatSession / clearChatHistory), not in localStorage, so it follows
 * them across devices instead of being stuck on one browser.
 */
async function loadHistory() {
  if (!sessionStorageService.get()) return [];
  try {
    const sessions = await authenticationApi.listChatHistory();
    return (sessions || []).map((s) => ({
      id: s.id,
      propertyId: s.propertyId,
      title: s.title,
      date: new Date(s.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }),
      preview: s.preview,
      messages: s.messages,
    }));
  } catch {
    return [];
  }
}
async function saveSession(property, messages) {
  if (!sessionStorageService.get()) return;
  try {
    await authenticationApi.saveChatSession({
      propertyId: property?.id,
      title: property?.title?.slice(0, 40) || "Property Chat",
      preview: messages.find(m => m.role === "user")?.content?.slice(0, 60) || "",
      messages,
    });
  } catch {}
}

function loadJsPDF() {
  return new Promise((resolve, reject) => {
    if (window.jspdf) { resolve(window.jspdf.jsPDF); return; }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    s.onload = () => resolve(window.jspdf.jsPDF);
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

async function generatePropertyPDF(property, selections) {
  const JsPDF = await loadJsPDF();
  const doc   = new JsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210, M = 18;

  const navy   = [27, 58, 107];
  const orange = [249, 115, 22];
  const white  = [255, 255, 255];
  const gray   = [100, 116, 139];
  const light  = [248, 250, 252];
  const dark   = [15, 23, 42];

  const price = formatPrice(property?.price);
  let y = 0;

  doc.setFillColor(...navy);
  doc.rect(0, 0, W, 38, "F");
  doc.setFontSize(7); doc.setTextColor(...white);
  doc.text("ONE GLOBAL MARKETPLACE", M, 10);
  doc.setFontSize(16); doc.setFont("helvetica", "bold");
  doc.text("Property Report", M, 22);
  doc.setFontSize(8); doc.setFont("helvetica", "normal");
  doc.text("www.oneglobalmarketplace.com", M, 32);
  doc.setFillColor(...orange);
  doc.rect(W - 45, 10, 35, 18, "F");
  doc.setFontSize(7); doc.setFont("helvetica", "bold"); doc.setTextColor(...white);
  doc.text("RERA", W - 40, 17);
  doc.text(property.reraApproved ? "APPROVED" : "PENDING", W - 40, 24);

  y = 50;

  doc.setFontSize(17); doc.setFont("helvetica", "bold"); doc.setTextColor(...dark);
  const titleLines = doc.splitTextToSize(property.title, W - 2 * M);
  doc.text(titleLines, M, y); y += titleLines.length * 7 + 3;

  doc.setFontSize(10); doc.setTextColor(...gray);
  doc.text("📍 " + (property.location || ""), M, y); y += 7;

  doc.setFontSize(20); doc.setFont("helvetica", "bold"); doc.setTextColor(...navy);
  doc.text(price, M, y);
  if (property.reraApproved) {
    doc.setFillColor(240, 253, 244);
    doc.roundedRect(M + 52, y - 7, 32, 9, 2, 2, "F");
    doc.setFontSize(7); doc.setTextColor(21, 128, 61);
    doc.text("RERA Approved", M + 54, y - 1);
  }
  y += 12;

  doc.setDrawColor(...orange); doc.setLineWidth(0.7);
  doc.line(M, y, W - M, y); y += 8;

  const specs = [
    ["Bedrooms",   property.bedrooms    || "N/A"],
    ["Bathrooms",  property.bathrooms   || "N/A"],
    ["Area",       (property.builtupArea || property.sqft || "N/A") + " sqft"],
    ["Parking",    property.parking     || "N/A"],
    ["Facing",     property.facing      || "N/A"],
    ["Furnishing", property.furnishing  || "N/A"],
  ];
  const boxW = (W - 2 * M - 8) / 3, boxH = 20;
  specs.forEach(([label, val], i) => {
    const col = i % 3, row = Math.floor(i / 3);
    const bx = M + col * (boxW + 4), by = y + row * (boxH + 4);
    doc.setFillColor(...light); doc.roundedRect(bx, by, boxW, boxH, 2, 2, "F");
    doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.3); doc.roundedRect(bx, by, boxW, boxH, 2, 2, "S");
    doc.setFontSize(7); doc.setTextColor(...gray); doc.setFont("helvetica", "normal");
    doc.text(label.toUpperCase(), bx + 4, by + 7);
    doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(...dark);
    doc.text(String(val), bx + 4, by + 15);
  });
  y += 2 * (boxH + 4) + 8;

  const section = (title) => {
    doc.setFillColor(...navy); doc.rect(M, y, 4, 8, "F");
    doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(...dark);
    doc.text(title, M + 7, y + 6); y += 13;
  };

  if (selections.includes("overview") && property.description) {
    section("Property Overview");
    doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(55, 65, 81);
    const lines = doc.splitTextToSize(property.description, W - 2 * M);
    doc.text(lines, M, y); y += lines.length * 5 + 8;
  }

  if (selections.includes("details")) {
    section("Property Details");
    const rows = [
      ["Type",          property.type             || "N/A"],
      ["Built-up Area", property.builtupArea       || "N/A"],
      ["Carpet Area",   property.carpetArea        || "N/A"],
      ["Maintenance",   property.maintenance       || "N/A"],
      ["Developer",     property.developerName     || "N/A"],
      ["Possession",    property.possessionStatus  || "N/A"],
    ];
    rows.forEach(([lbl, val], i) => {
      const rowY = y + i * 9;
      if (i % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(M, rowY - 3, W - 2 * M, 9, "F"); }
      doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(...gray);
      doc.text(lbl, M + 2, rowY + 3);
      doc.setFont("helvetica", "bold"); doc.setTextColor(...dark);
      doc.text(String(val), W / 2, rowY + 3);
    });
    y += rows.length * 9 + 8;
  }

  if (selections.includes("amenities") && property.amenities?.length) {
    section("Features & Amenities");
    const cols = 3, gap = 4;
    const aw = (W - 2 * M - gap * (cols - 1)) / cols;
    property.amenities.forEach((a, i) => {
      const col = i % cols, row = Math.floor(i / cols);
      const ax = M + col * (aw + gap), ay = y + row * 9;
      doc.setFillColor(239, 246, 255); doc.roundedRect(ax, ay - 3, aw, 8, 1, 1, "F");
      doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...navy);
      doc.text("• " + a, ax + 3, ay + 2);
    });
    y += Math.ceil(property.amenities.length / cols) * 9 + 8;
  }

  if (selections.includes("contact")) {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setFillColor(...navy); doc.rect(M, y, W - 2 * M, 40, "F");
    doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(...white);
    doc.text("Contact Us", M + 8, y + 10);
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    doc.text("📞 +91 83091 20616", M + 8, y + 20);
    doc.text("🌐 www.oneglobalmarketplace.com", M + 8, y + 28);
    doc.text("📍 Bengaluru, Karnataka, India", M + 8, y + 36);
    y += 50;
  }

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFillColor(...light); doc.rect(0, 285, W, 12, "F");
    doc.setDrawColor(...orange); doc.setLineWidth(0.5); doc.line(0, 285, W, 285);
    doc.setFontSize(7); doc.setTextColor(...gray);
    doc.text("One Global Marketplace — Trusted Real Estate Discovery", M, 291);
    doc.text("Page " + i + " of " + pageCount, W - M - 12, 291);
    doc.text("Generated: " + new Date().toLocaleDateString("en-IN"), W / 2 - 10, 291);
  }

  doc.save(`OGM-${property.title?.slice(0, 25).replace(/\s+/g, "-") || "Property"}.pdf`);
}

export default function AskDiscoverWidget({ property }) {
  const [phase,         setPhase]         = useState("pill");
  const [input,         setInput]         = useState("");
  const [messages,      setMessages]      = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [visible,       setVisible]       = useState(false);
  const [expanded,      setExpanded]      = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showHistory,   setShowHistory]   = useState(false);
  const [history,       setHistory]       = useState([]);
  const [pdfMode,       setPdfMode]       = useState(null);
  const [emiMode,       setEmiMode]       = useState(false);
  const [saved,         setSaved]         = useState(false);
  const [welcomed,      setWelcomed]      = useState(false);
  const [pdfSelections, setPdfSelections] = useState(["overview","details","amenities","contact"]);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const chatIdRef      = useRef(`prop-${property?.id}-${Date.now()}`);
  const inputRef       = useRef(null);
  const bottomRef      = useRef(null);
  const timerRef       = useRef(null);
  const hasSentInitial = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    loadHistory().then((h) => { if (!cancelled) setHistory(h); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setTimeout(() => setVisible(true), 800);
    if (phase !== "pill") return;
    const cycle = () => {
      setExpanded(true);
      timerRef.current = setTimeout(() => {
        setExpanded(false);
        timerRef.current = setTimeout(cycle, 3500);
      }, 4000);
    };
    timerRef.current = setTimeout(cycle, 1800);
    return () => clearTimeout(timerRef.current);
  }, [phase]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => { if (phase === "search") setTimeout(() => inputRef.current?.focus(), 200); }, [phase]);

  const send = useCallback(async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;

    const token = sessionStorageService.get();
    if (!token) {
      navigate(AUTH_ROUTES.LOGIN, {
        state: { redirectTo: location.pathname, pendingQuery: q },
      });
      return;
    }

    setInput("");
    setPhase("chat");
    setWelcomed(true);

    if (isEmiQuery(q)) {
      setMessages(prev => [...prev,
        { role: "user", content: q },
        { role: "assistant", content: null, isEmi: true },
      ]);
      setLoading(false);
      return;
    }

    if (isPdfQuery(q) && pdfMode === null) {
      setPdfMode("asking");
      setMessages(prev => [
        ...prev,
        { role: "user", content: q },
        { role: "assistant", content: null, isPdfAsk: true },
      ]);
      setLoading(false);
      return;
    }

    const isNearby = isNearbyQuery(q);
    const isDist   = isDistanceQuery(q);
    const nInfo    = isNearby ? extractNearbyType(q) : {};

    const propLat = property?.latitude  ?? property?.lat  ?? property?.coordinates?.lat  ?? null;
    const propLng = property?.longitude ?? property?.lng  ?? property?.coordinates?.lng
        ?? property?.coordinates?.long ?? null;
    const hasPropCoords = propLat !== null && propLng !== null
        && !isNaN(Number(propLat)) && !isNaN(Number(propLng));

    let mapOriginText = property?.location || "";
    let mapDestText   = null;
    let userOriginText = null;

    if (isDist) {
      const extracted = extractDestination(q);
      if (extracted.isUserLocation) {
        userOriginText = extracted.place;
        mapDestText    = property?.location;
      } else if (extracted.place) {
        mapDestText = extracted.place;
      }
    }

    const recentMapExists = messages.slice(-6).some(m => m.needsMap);
    const shouldShowMap = (isNearby && nInfo.keyword) ||
        (isDist && (mapDestText || userOriginText)) ||
        (isDist && !recentMapExists);

    const userMsg = {
      role: "user",
      content: q,
      needsMap:        shouldShowMap,
      destination:     mapDestText,
      mapOrigin:       userOriginText || mapOriginText,
      propLat:         hasPropCoords ? Number(propLat) : null,
      propLng:         hasPropCoords ? Number(propLng) : null,
      isUserOrigin:    !!userOriginText,
      nearbyType:      isNearby ? nInfo.type : null,
      nearbyKeyword:   isNearby ? nInfo.keyword : null,
    };

    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const history = buildHistory(messages);
      const reply = await askGemini(q, chatIdRef.current, buildPrompt(property), history, token);
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      if (err.message === "401") {
        // Token expired/invalid mid-session - send them to log back in
        // rather than showing a generic error message.
        navigate(AUTH_ROUTES.LOGIN, {
          state: { redirectTo: location.pathname, pendingQuery: q },
        });
        return;
      }
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Having trouble connecting. Please try again.",
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, property, navigate, location.pathname]);

  // Resumes a question that was interrupted by the login redirect: the
  // AuthenticationPage hands it back as location.state.question once the
  // person signs in, matching the same convention AiSearchPage uses.
  useEffect(() => {
    const pending = location.state?.question;
    if (pending && !hasSentInitial.current) {
      hasSentInitial.current = true;
      navigate(location.pathname, { replace: true, state: {} });
      send(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const price    = formatPrice(property?.price);
  const thumbImg = property?.mainImages?.[0] || property?.images?.[0] || property?.image;

  const widget = (
      <>
        {phase === "pill" && (
            <div
                className={`adw-pill-trigger ${visible ? "adw-pill-visible" : ""}`}
                onClick={() => setPhase("search")}
            >
              <div className={`adw-pill-text ${expanded ? "adw-pill-text-expanded" : ""}`}>
                <span className="adw-pill-label">Ask. Discover. Invest...</span>
              </div>
              <div className="adw-pill-robot-wrap">
                <img src={ROBOT_IMG} alt="AI" className="adw-pill-robot" />
                <span className="adw-online-dot" />
              </div>
            </div>
        )}

        {phase === "search" && (
            <>
              <div className="adw-search-bar-wrap">
                <div className="adw-search-bar">
                  <img src={ROBOT_IMG} alt="AI" className="adw-search-robot" />
                  <input
                      ref={inputRef}
                      className="adw-search-input"
                      placeholder="Have more questions? Ask your AI Property Advisor..."
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={handleKey}
                  />
                  <button className="adw-search-btn" onClick={() => send()} disabled={!input.trim()}>
                    <svg width="20" height="20" fill="none" stroke="#fff" strokeWidth="2.5" viewBox="0 0 24 24">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="m21 21-4.35-4.35"/>
                    </svg>
                  </button>
                </div>
                <div className="adw-search-suggestions">
                  {DEFAULT_SUGGESTIONS.map(s => (
                      <button key={s} className="adw-search-chip" onClick={() => send(s)}>{s}</button>
                  ))}
                </div>
                <button className="adw-search-close" onClick={() => setPhase("pill")}>✕ Close</button>
              </div>
              <div className="adw-backdrop" onClick={() => setPhase("pill")} />
            </>
        )}

        {phase === "chat" && (
            <div className="adw-chat-overlay">
              {showHistory && (
                  <div className="adw-history-sidebar">
                    <div className="adw-history-header">
                      <span>Chat History</span>
                      <button className="adw-history-close" onClick={() => setShowHistory(false)}>✕</button>
                    </div>
                    <div className="adw-history-list">
                      {history.length === 0 && (
                          <div className="adw-history-empty">
                            {sessionStorageService.get()
                                ? "No previous chats yet"
                                : "Sign in to save and revisit your chat history"}
                          </div>
                      )}
                      {history.map(session => (
                          <div
                              key={session.id}
                              className="adw-history-item"
                              onClick={() => { setMessages(session.messages); setShowHistory(false); }}
                          >
                            <div className="adw-history-title">{session.title}</div>
                            <div className="adw-history-preview">{session.preview}</div>
                            <div className="adw-history-date">{session.date}</div>
                          </div>
                      ))}
                    </div>
                    {history.length > 0 && (
                        <button
                            className="adw-history-clear"
                            onClick={async () => {
                              try { await authenticationApi.clearChatHistory(); } catch {}
                              setHistory([]);
                            }}
                        >
                          Clear all history
                        </button>
                    )}
                  </div>
              )}

              <div className="adw-chat-inner">

                {!selectedPlace && (
                    <div className="adw-chat-topbar">
                      <div className="adw-chat-topbar-left">
                        <img src={ROBOT_IMG} alt="AI" className="adw-chat-topbar-robot" />
                        <div>
                          <div className="adw-chat-topbar-title">AI Property Advisor</div>
                          <div className="adw-chat-topbar-subtitle">Ask me anything about this property</div>
                        </div>
                      </div>
                      <div className="adw-chat-topbar-right">
                        <button className="adw-chat-topbar-btn" onClick={() => setShowHistory(v => !v)} title="Chat history">
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                          </svg>
                        </button>
                        <button className="adw-chat-topbar-btn close" title="Close" onClick={async () => {
                          if (messages.length > 0) {
                            await saveSession(property, messages);
                            setHistory(await loadHistory());
                          }
                          setPhase("pill"); setMessages([]); setSelectedPlace(null); setPdfMode(null); setWelcomed(false);
                        }}>✕</button>
                      </div>
                    </div>
                )}

                {!welcomed && messages.length === 0 && (
                    <div className="adw-welcome">
                      <img src={ROBOT_IMG} alt="AI" className="adw-msg-avatar" />
                      <div className="adw-welcome-bubble">
                        Hi! I'm your <strong>AI Property Advisor</strong> for OGM. Ask about price, amenities, nearby places, routes, or get a PDF report instantly.
                        <div className="adw-welcome-chips">
                          {["Key highlights?","Is price negotiable?","Calculate EMI","Schools nearby?"].map(s => (
                              <button key={s} className="adw-welcome-chip" onClick={() => { setWelcomed(true); send(s); }}>{s}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                )}

                {property && (
                    <div className="adw-mini-card">

                      <div className="adw-mini-img-wrap">
                        <MiniImageCarousel
                            images={property.mainImages || property.images || [thumbImg].filter(Boolean)}
                            title={property.title}
                        />
                        <div className="adw-mini-img-gradient" />
                        <div className="adw-mini-img-top">
                    <span className={`adw-mini-rera-badge ${property.reraApproved ? "approved" : "pending"}`}>
                      {property.reraApproved ? "✓ RERA Approved" : "RERA Pending"}
                    </span>
                          <span className="adw-mini-type-badge">{property.type}</span>
                        </div>
                        {(() => {
                          const rawP = (() => {
                            if (property.priceRaw && !isNaN(Number(property.priceRaw))) return Number(property.priceRaw);
                            const s = String(property.price || "").replace(/[₹,\s]/g, "");
                            if (/cr/i.test(s)) return parseFloat(s) * 10000000;
                            if (/l$/i.test(s))  return parseFloat(s) * 100000;
                            return parseFloat(s) || 0;
                          })();
                          const area = Number(property.builtupArea) || Number(property.sqft) || 0;
                          const pps  = rawP > 0 && area > 0 ? Math.round(rawP / area) : 0;
                          return (
                              <div className="adw-mini-price-overlay">
                                <span className="adw-mini-price-big">{price}</span>
                                {pps > 0 && (
                                    <span className="adw-mini-price-sqft">
                            ₹{pps.toLocaleString("en-IN")}/sqft
                          </span>
                                )}
                              </div>
                          );
                        })()}
                      </div>

                      <div className="adw-mini-info">
                        <div className="adw-mini-loc">
                          <svg width="10" height="10" fill="#f97316" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>
                          {property.location}
                        </div>

                        <div className="adw-mini-title">{property.title}</div>

                        <div className="adw-mini-spec-pills">
                          {property.bedrooms && (
                              <span className="adw-mini-spec-pill">
                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 7v13M21 7v13M3 14h18M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2M5 12V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v3M13 12V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v3"/></svg>
                                {property.bedrooms} BHK
                      </span>
                          )}
                          {property.bathrooms && (
                              <span className="adw-mini-spec-pill">
                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 12h16M4 12a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2M4 12v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6"/></svg>
                                {property.bathrooms} Bath
                      </span>
                          )}
                          {(property.builtupArea || property.sqft) && (
                              <span className="adw-mini-spec-pill">
                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                                {property.builtupArea || property.sqft} sqft
                      </span>
                          )}
                          {property.parking && (
                              <span className="adw-mini-spec-pill">
                        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2M7 14h10"/></svg>
                                {property.parking} Parking
                      </span>
                          )}
                        </div>

                        {(() => {
                          const contactNum = property.contactNumber
                              || property.agentPhone
                              || property.agentMobile
                              || property.phone
                              || property.mobile
                              || property.builderPhone
                              || "+918309120616";
                          const waNum = contactNum.replace(/[^0-9]/g, "");
                          const waMsg = encodeURIComponent(`Hi, I'm interested in ${property.title} listed on OGM. Please share more details.`);
                          return (
                              <div className="adw-mini-actions-row">
                                <a href={`tel:${contactNum}`} className="adw-mini-btn primary">
                                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.6 3.27 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.54a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                                  Call
                                </a>
                                <a href={`https://wa.me/${waNum}?text=${waMsg}`} target="_blank" rel="noreferrer" className="adw-mini-btn whatsapp">
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                  WhatsApp
                                </a>
                                <div className="adw-mini-btn-group">
                                  <button
                                      className={`adw-mini-btn-icon${saved ? " saved" : ""}`}
                                      title={saved ? "Saved!" : "Save property"}
                                      onClick={() => setSaved(v => !v)}
                                  >
                                    <svg width="14" height="14"
                                         fill={saved ? "#ef4444" : "none"}
                                         stroke={saved ? "#ef4444" : "currentColor"}
                                         strokeWidth="2" viewBox="0 0 24 24">
                                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                                    </svg>
                                  </button>
                                  <button className="adw-mini-btn-icon" title="View property" onClick={() => { setPhase("pill"); setMessages([]); }}>
                                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                  </button>
                                  <button className="adw-mini-btn-icon" title="Calculate EMI" onClick={() => send("Calculate EMI")}>
                                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="12" y2="14"/></svg>
                                  </button>
                                </div>
                              </div>
                          );
                        })()}
                      </div>

                    </div>
                )}

                <div className="adw-chat-messages">
                  {messages.map((msg, i) => (
                      <div key={i}>
                        {msg.role === "user" && (
                            <div className="adw-user-q">{msg.content}</div>
                        )}

                        {msg.role === "assistant" && !msg.isPdfAsk && msg.content && (
                            <div className="adw-assistant-msg">
                              <img src={ROBOT_IMG} alt="AI" className="adw-msg-avatar" />
                              <div className="adw-msg-text">{parseMarkdown(msg.content)}</div>
                            </div>
                        )}

                        {msg.role === "assistant" && msg.isEmi && (
                            <div className="adw-assistant-msg">
                              <img src={ROBOT_IMG} alt="AI" className="adw-msg-avatar" />
                              <EmiCalculator propertyPrice={property?.priceRaw ?? property?.price} />
                            </div>
                        )}

                        {msg.role === "assistant" && msg.isPdfAsk && (
                            <div className="adw-assistant-msg">
                              <img src={ROBOT_IMG} alt="AI" className="adw-msg-avatar" />
                              <div className="adw-pdf-ask-card">
                                <div className="adw-pdf-ask-title">What should I include in the document?</div>
                                <div className="adw-pdf-options">
                                  {[
                                    { key: "overview",  label: "📝 Property Overview" },
                                    { key: "details",   label: "📋 Property Details" },
                                    { key: "amenities", label: "✨ Features & Amenities" },
                                    { key: "contact",   label: "📞 Contact Information" },
                                  ].map(({ key, label }) => (
                                      <label key={key} className={`adw-pdf-opt ${pdfSelections.includes(key) ? "selected" : ""}`}>
                                        <input
                                            type="checkbox"
                                            checked={pdfSelections.includes(key)}
                                            onChange={e =>
                                                setPdfSelections(prev =>
                                                    e.target.checked ? [...prev, key] : prev.filter(k => k !== key)
                                                )
                                            }
                                        />
                                        {label}
                                      </label>
                                  ))}
                                </div>
                                <button
                                    className="adw-pdf-generate-btn"
                                    disabled={pdfSelections.length === 0 || pdfGenerating}
                                    onClick={async () => {
                                      setPdfGenerating(true);
                                      setMessages(prev => [...prev, {
                                        role: "user",
                                        content: "Generate the PDF with: " + pdfSelections.join(", "),
                                      }]);
                                      try {
                                        await generatePropertyPDF(property, pdfSelections);
                                        setMessages(prev => [...prev, {
                                          role: "assistant",
                                          content: "✅ Your PDF has been downloaded! It includes: " + pdfSelections.join(", ") + ". Let me know if you need any other information.",
                                        }]);
                                      } catch {
                                        setMessages(prev => [...prev, {
                                          role: "assistant",
                                          content: "Sorry, there was an issue generating the PDF. Please try again.",
                                        }]);
                                      } finally {
                                        setPdfGenerating(false);
                                        setPdfMode(null);
                                      }
                                    }}
                                >
                                  {pdfGenerating ? "⏳ Generating PDF…" : "⬇ Download PDF"}
                                </button>
                              </div>
                            </div>
                        )}

                        {msg.role === "assistant" && messages[i - 1]?.needsMap && (
                            <SmartMap
                                origin={messages[i - 1].mapOrigin || property?.location}
                                destination={messages[i - 1].destination}
                                nearbyType={messages[i - 1].nearbyType}
                                nearbyKeyword={messages[i - 1].nearbyKeyword}
                                onPlaceClick={(pl, num) => setSelectedPlace({ place: pl, number: num })}
                                propLat={messages[i - 1].propLat}
                                propLng={messages[i - 1].propLng}
                                isUserOrigin={messages[i - 1].isUserOrigin}
                            />
                        )}
                      </div>
                  ))}

                  {loading && (
                      <div className="adw-assistant-msg">
                        <img src={ROBOT_IMG} alt="AI" className="adw-msg-avatar" />
                        <div className="adw-typing"><span/><span/><span/></div>
                      </div>
                  )}
                  <div ref={bottomRef} />
                </div>

                {!loading && messages.length > 0 && (
                    <div className="adw-followups">
                      {POST_QUERY_SUGGESTIONS.map(s => (
                          <button key={s} className="adw-followup-chip" onClick={() => send(s)}>{s}</button>
                      ))}
                    </div>
                )}

                <div className="adw-chat-input-wrap">
                  <AiBottomSearchBar onSend={(text) => send(text)} />
                </div>

                {selectedPlace && (
                    <PlacePanel
                        place={selectedPlace.place}
                        number={selectedPlace.number}
                        onClose={() => setSelectedPlace(null)}
                    />
                )}
              </div>


            </div>
        )}
      </>
  );

  return createPortal(widget, document.body);
}
